<template>
  <div class="demand-insights-heatmap">
    <div class="map-container">
      <div class="d-flex">
        <div class="flex-grow-1">
          <USStateMap
            :state-demand-counts="stateDemandCounts"
            :state-campaign-details="stateCampaignDetails"
            :selected-day-label="selectedDayName"
            :selected-time-label="formattedTimeOnly"
            :peak-campaign-count="peakCampaignCount"
            :default-margins="{ min: 0, max: 0 }"
            :show-territories="true"
          />
          <div class="mt-2 text-muted small">
            <span class="color-legend">
              <span class="legend-item">
                <span
                  class="legend-color"
                  style="background: #e5e7eb;"
                /> No demand
              </span>
              <span class="legend-item">
                <span
                  class="legend-color"
                  style="background: #bbf7d0;"
                /> Low
              </span>
              <span class="legend-item">
                <span
                  class="legend-color"
                  style="background: #fed7aa;"
                /> Medium
              </span>
              <span class="legend-item">
                <span
                  class="legend-color"
                  style="background: #fecaca;"
                /> High
              </span>
            </span>
          </div>
        </div>
        <div class="filters-sidebar ml-3">
          <div
            v-if="zipcodesLoading"
            class="small text-muted mb-2"
          >
            Preparing zipcodes...
          </div>
          <button
            v-else-if="combinedZipcodes.length"
            type="button"
            class="btn btn-sm btn-zipcode-download mb-2"
            title="Combined list of zip codes lists uploaded to buyer campaigns"
            @click="$emit('download-zipcodes')"
          >
            Download {{ combinedZipcodes.length }} zipcodes
          </button>
          <div
            v-click-outside="closeTsDropdown"
            class="filter-group mb-3"
          >
            <label class="font-weight-bold small mb-1">Traffic Sources</label>
            <div class="filter-dropdown">
              <button
                type="button"
                class="btn btn-secondary btn-sm w-100 text-left d-flex justify-content-between align-items-center"
                @click="tsDropdownOpen = !tsDropdownOpen"
              >
                <span>{{ selectedTrafficSources.length }} of {{ allTrafficSources.length }}</span>
                <CIcon
                  name="cil-chevron-bottom"
                  size="sm"
                />
              </button>
              <div
                v-show="tsDropdownOpen"
                class="filter-dropdown-menu"
              >
                <div class="mb-1 d-flex">
                  <a
                    href
                    class="small mr-2"
                    @click.prevent="selectAllTrafficSources"
                  >All</a>
                  <a
                    href
                    class="small"
                    @click.prevent="clearTrafficSources"
                  >None</a>
                </div>
                <div
                  v-for="ts in allTrafficSources"
                  :key="ts"
                  class="form-check"
                >
                  <input
                    :id="'ts-' + ts"
                    v-model="selectedTrafficSources"
                    type="checkbox"
                    class="form-check-input"
                    :value="ts"
                  >
                  <label
                    :for="'ts-' + ts"
                    class="form-check-label small"
                  >{{ ts }}</label>
                </div>
              </div>
            </div>
            <div
              v-if="selectedTrafficSources.length"
              class="selected-items small text-muted mt-1"
            >
              {{ selectedTrafficSources.join(', ') }}
            </div>
          </div>

          <div
            v-click-outside="closeStatesDropdown"
            class="filter-group"
          >
            <label class="font-weight-bold small mb-1">US States</label>
            <div class="filter-dropdown">
              <button
                type="button"
                class="btn btn-secondary btn-sm w-100 text-left d-flex justify-content-between align-items-center"
                @click="statesDropdownOpen = !statesDropdownOpen"
              >
                <span>{{ selectedStates.length }} of {{ allStates.length }}</span>
                <CIcon
                  name="cil-chevron-bottom"
                  size="sm"
                />
              </button>
              <div
                v-show="statesDropdownOpen"
                class="filter-dropdown-menu"
              >
                <div class="mb-1 d-flex">
                  <a
                    href
                    class="small mr-2"
                    @click.prevent="selectAllStates"
                  >All</a>
                  <a
                    href
                    class="small"
                    @click.prevent="clearStates"
                  >None</a>
                </div>
                <div
                  v-for="st in allStates"
                  :key="st"
                  class="form-check"
                >
                  <input
                    :id="'st-' + st"
                    v-model="selectedStates"
                    type="checkbox"
                    class="form-check-input"
                    :value="st"
                  >
                  <label
                    :for="'st-' + st"
                    class="form-check-label small"
                  >{{ st }}</label>
                </div>
              </div>
            </div>
            <div
              v-if="selectedStates.length"
              class="selected-items small text-muted mt-1"
            >
              {{ selectedStates.join(', ') }}
            </div>
          </div>
        </div>
      </div>
      <div class="mt-3">
        <DemandTimeline
          :bar-heights="barHeights"
          :selected-index="selectedSlotIndex"
          :time-label="formattedSelectedTime"
          @update:selectedIndex="selectedSlotIndex = $event"
        />
      </div>
    </div>
  </div>
</template>

<script>
import USStateMap from '@/components/USStateMap.vue'
import DemandTimeline from '@/components/DemandTimeline.vue'

import { SLOTS_PER_DAY, TOTAL_SLOTS } from '@/store/modules/insights.store'

// Display order: Sun, Mon, Tue, Wed, Thu, Fri, Sat
// dailySchedule index: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
const DISPLAY_TO_SCHEDULE = [0, 1, 2, 3, 4, 5, 6]
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const ALL_US_STATES = [
  'AK', 'AL', 'AR', 'AS', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL',
  'FM', 'GA', 'GU', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA',
  'MA', 'MD', 'ME', 'MH', 'MI', 'MN', 'MO', 'MP', 'MS', 'MT', 'NC',
  'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA',
  'PR', 'PW', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VI', 'VT',
  'WA', 'WI', 'WV', 'WY'
]

export default {
  name: 'DemandInsightsHeatmap',

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) {
            binding.value()
          }
        }

        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },

  components: {
    USStateMap,
    DemandTimeline
  },

  props: {
    insight: {
      type: Object,
      default: null
    },
    peakCampaignCount: {
      type: Number,
      default: 0
    },
    combinedZipcodes: {
      type: Array,
      default: () => []
    },
    zipcodesLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedSlotIndex: 72,
      selectedTrafficSources: [],
      selectedStates: [],
      tsDropdownOpen: false,
      statesDropdownOpen: false
    }
  },

  computed: {
    campaigns() {
      return this.insight?.campaigns || []
    },

    allTrafficSources() {
      const set = new Set()
      this.campaigns.forEach(s => {
        if (s.trafficSourceNames) {
          s.trafficSourceNames.forEach(name => set.add(name))
        }
      })
      return Array.from(set).sort()
    },

    allStates() {
      const set = new Set()
      this.campaigns.forEach(s => {
        this.getStatesFromCampaign(s).forEach(code => set.add(code))
      })
      return Array.from(set).sort()
    },

    selectedDay() {
      return Math.floor(this.selectedSlotIndex / SLOTS_PER_DAY)
    },

    selectedHalfHour() {
      return this.selectedSlotIndex % SLOTS_PER_DAY
    },

    selectedScheduleDayIndex() {
      return DISPLAY_TO_SCHEDULE[this.selectedDay]
    },

    selectedHour() {
      return Math.floor(this.selectedHalfHour / 2)
    },

    selectedMinute() {
      return (this.selectedHalfHour % 2) * 30
    },

    selectedDayName() {
      return DAY_NAMES[this.selectedDay]
    },

    formattedTimeOnly() {
      const h = this.selectedHour.toString().padStart(2, '0')
      const m = this.selectedMinute.toString().padStart(2, '0')
      return `${h}:${m}`
    },

    formattedSelectedTime() {
      return `${this.selectedDayName} ${this.formattedTimeOnly}`
    },

    matchingCampaignsForSelected() {
      return this.getMatchingCampaigns(this.selectedScheduleDayIndex, this.selectedHour, this.selectedMinute)
    },

    stateDemandCounts() {
      const counts = {}
      this.matchingCampaignsForSelected.forEach(campaign => {
        const states = this.getStatesFromCampaign(campaign)
        states.forEach(code => {
          if (this.selectedStates.includes(code)) {
            counts[code] = (counts[code] || 0) + 1
          }
        })
      })
      return counts
    },

    allCampaignsForSelectedTime() {
      const timeMinutes = this.selectedHour * 60 + this.selectedMinute
      return this.campaigns.filter(campaign =>
        this.matchesTime(campaign, this.selectedScheduleDayIndex, timeMinutes)
      )
    },

    stateCampaignDetails() {
      const details = {}
      const dayIdx = this.selectedScheduleDayIndex
      this.allCampaignsForSelectedTime.forEach(campaign => {
        if (!this.matchesTrafficSource(campaign)) {
          return
        }

        const states = this.getStatesFromCampaign(campaign)
        const sources = campaign.trafficSourceNames && campaign.trafficSourceNames.length
          ? campaign.trafficSourceNames
            .map(name => ({
              name,
              selected: this.selectedTrafficSources.includes(name)
            }))
            .sort((a, b) => b.selected - a.selected)
          : [{ name: 'All traffic sources', selected: false }]
        const hours = this.getCampaignHours(campaign, dayIdx)
        states.forEach(code => {
          if (!details[code]) {
            details[code] = []
          }

          const budgetObj = campaign.budget
          let budgetValue = null
          if (budgetObj && budgetObj.dailyBudgetInUSD) {
            budgetValue = budgetObj.dailyBudgetInUSD
          }

          const loc = campaign.location
          let locationType = 'NW'
          if (loc?.zipCodes?.length) {
            locationType = 'Z'
          } else if (loc?.states?.length && !loc.isNationwide) {
            locationType = 'S'
          }

          let spendBudgetPercent = null
          let spendBudgetZero = false
          let weeklySpendData = null
          if (campaign.weeklySpendBudget && campaign.weeklySpendBudget.length === 7 && budgetObj && budgetObj.dailyBudgetInUSD) {
            const dailyBudget = budgetObj.dailyBudgetInUSD
            const days = campaign.weeklySpendBudget.map(spent => {
              if (spent === 0) {
                return { percent: 0, zero: true }
              }

              let p = (spent / dailyBudget) * 100
              if (p < 0) {
                p = p + 100
              }

              return { percent: Math.round(p), zero: false }
            })

            const nonZeroDays = days.filter(d => !d.zero)
            const allZero = nonZeroDays.length === 0
            const avg = allZero ? 0 : Math.round(nonZeroDays.reduce((sum, d) => sum + d.percent, 0) / nonZeroDays.length)
            spendBudgetPercent = avg
            spendBudgetZero = allZero

            weeklySpendData = { dailyBudget, days }
          }

          details[code].push({ sources, hours, campaignId: campaign.campaignId, budget: budgetValue, locationType, spendBudgetPercent, spendBudgetZero, weeklySpendData })
        })
      })
      for (const code in details) {
        details[code].sort((a, b) => (b.budget || 0) - (a.budget || 0))
      }

      return details
    },

    barHeights() {
      const heights = new Array(TOTAL_SLOTS).fill(0)
      for (let i = 0; i < TOTAL_SLOTS; i++) {
        const displayDay = Math.floor(i / SLOTS_PER_DAY)
        const halfHour = i % SLOTS_PER_DAY
        const scheduleDayIdx = DISPLAY_TO_SCHEDULE[displayDay]
        const hour = Math.floor(halfHour / 2)
        const minute = (halfHour % 2) * 30
        heights[i] = this.getMatchingCampaigns(scheduleDayIdx, hour, minute).length
      }

      return heights
    }
  },

  watch: {
    allTrafficSources: {
      immediate: true,
      handler(sources) {
        this.selectedTrafficSources = [...sources]
      }
    },
    allStates: {
      immediate: true,
      handler(states) {
        this.selectedStates = [...states]
      }
    },
    selectedTrafficSources() {
      this.emitFilterChange()
    },
    selectedStates() {
      this.emitFilterChange()
    }
  },

  methods: {
    getMatchingCampaigns(scheduleDayIdx, hour, minute) {
      const timeMinutes = hour * 60 + minute
      return this.campaigns.filter(campaign => {
        if (!this.matchesTrafficSource(campaign)) {
          return false
        }

        if (!this.matchesSelectedStates(campaign)) {
          return false
        }

        return this.matchesTime(campaign, scheduleDayIdx, timeMinutes)
      })
    },

    matchesSelectedStates(campaign) {
      if (!this.selectedStates.length) {
        return false
      }

      const states = this.getStatesFromCampaign(campaign)
      if (!states.length) {
        return false
      }

      return states.some(code => this.selectedStates.includes(code))
    },

    matchesTrafficSource(campaign) {
      if (!this.selectedTrafficSources.length) {
        return false
      }

      if (!campaign.trafficSourceNames || !campaign.trafficSourceNames.length) {
        return true
      }

      return campaign.trafficSourceNames.some(name =>
        this.selectedTrafficSources.includes(name)
      )
    },

    matchesTime(campaign, scheduleDayIdx, timeMinutes) {
      if (campaign.isAllHours) {
        return true
      }

      if (!campaign.dailySchedule || !campaign.dailySchedule[scheduleDayIdx]) {
        return false
      }

      const day = campaign.dailySchedule[scheduleDayIdx]
      if (day.isClosed) {
        return false
      }

      if (!day.openTime || !day.closeTime) {
        return false
      }

      const open = day.openTime.hour * 60 + day.openTime.minute
      const close = day.closeTime.hour * 60 + day.closeTime.minute
      return timeMinutes >= open && timeMinutes < close
    },

    getCampaignHours(campaign, scheduleDayIdx) {
      if (campaign.isAllHours) {
        return 'All hours'
      }

      const day = campaign.dailySchedule?.[scheduleDayIdx]
      if (!day || day.isClosed || !day.openTime || !day.closeTime) {
        return 'Closed'
      }

      const oh = day.openTime.hour.toString().padStart(2, '0')
      const om = day.openTime.minute.toString().padStart(2, '0')
      const ch = day.closeTime.hour.toString().padStart(2, '0')
      const cm = day.closeTime.minute.toString().padStart(2, '0')
      return `${oh}:${om} - ${ch}:${cm}`
    },

    getStatesFromCampaign(campaign) {
      if (!campaign.location) {
        return []
      }

      if (campaign.location.isNationwide) {
        return ALL_US_STATES
      }

      return campaign.location.states || []
    },

    selectAllTrafficSources() {
      this.selectedTrafficSources = [...this.allTrafficSources]
    },

    clearTrafficSources() {
      this.selectedTrafficSources = []
    },

    selectAllStates() {
      this.selectedStates = [...this.allStates]
    },

    clearStates() {
      this.selectedStates = []
    },

    closeTsDropdown() {
      this.tsDropdownOpen = false
    },

    closeStatesDropdown() {
      this.statesDropdownOpen = false
    },

    emitFilterChange() {
      this.$emit('filter-change', {
        states: this.selectedStates.length === this.allStates.length ? null : this.selectedStates,
        trafficSources: this.selectedTrafficSources.length === this.allTrafficSources.length ? null : this.selectedTrafficSources
      })
    }
  }
}
</script>

<style scoped>
.demand-insights-heatmap {
  width: 100%;
}

.filters-sidebar {
  min-width: 180px;
  max-width: 220px;
}

.filter-group {
  position: relative;
}

.filter-dropdown {
  position: relative;
}

.filter-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  padding: 8px;
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.form-check {
  padding-left: 1.25rem;
}

.form-check-input {
  margin-top: 0.2rem;
}

.selected-items {
  max-height: 80px;
  overflow-y: auto;
  line-height: 1.4;
  word-break: break-word;
}

.map-container {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: #f9fafb;
}

.color-legend {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-color {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid #9ca3af;
}

.btn-zipcode-download {
  background: rgba(59, 130, 246, 0.12);
  color: rgba(59, 130, 246, 0.8);
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.btn-zipcode-download:hover {
  background: rgba(59, 130, 246, 0.2);
  color: rgba(59, 130, 246, 1);
}
</style>
